import React, { FormEvent, useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import html2pdf from 'html2pdf.js/dist/html2pdf.bundle.min';
import toast from 'react-hot-toast';
import { isAuthenticated } from '../../../sso';
import { text } from '../../data';
import { createProjectReports } from '../../api/api';
import { useProject } from '../../project-details/context/ProjectContext';
import SaveToProjectDropDown from '../SaveToProjectDropDown';
import TextInput from '../../../components/bearing-search/components/forms/TextInput';
import ToggleButton from './ToggleButton';
import Modal from '../../../components/common/Modal';
import Print from './Print';
import { saveDropDownOpenAnalyticsHandler, saveDropdownSelectionAnalyticsHandler, saveNewProjectClickAnalyticsHandler } from '../../analytics';

type SaveReportDropdownProps = {
	isResultsReady?: boolean;
	buttonStyle?: string;
	hideDropdown?: boolean;
	btnClass?: string;
	partNumber?: string;
	partImageUrls: string;
	partUrl?: string;
	partType?: string;
};

const SaveReportDropdown: React.FC<SaveReportDropdownProps> = (initialProps) => {
	const [ isLoggedIn, setIsLoggedIn ] = useState(false);
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ isLoading, setIsLoading ] = useState(false);
	const [ isOpen, setIsOpen ] = useState(false);
	const [ isModalOpen, setIsModalOpen ] = useState<boolean>(false);
	const [ isResultsReady, setIsResultsReady ] = useState(initialProps.isResultsReady || false);
	const [ closeText, setCloseText ] = useState<string>(text.cancel);
	const [ formError, setFormError ] = useState<boolean>(false);
	const [ formSuccess, setFormSuccess ] = useState<boolean>(false);
	const [ createErrorMessage, setCreateErrorMessage ] = useState<string>('');
	const { projects, refreshProjects, loading: projectsLoading } = useProject();
	const [ projectID, setProjectID ] = useState<string>('');
	const [ projectName, setProjectName ] = useState<string>('');
	const [ reportName, setReportName ] = useState<string>('');
	const [ comments, setComments ] = useState<string>('');
	const [ hasChanged, setHasChanged ] = useState(false);
	const [ partNumber, setPartNumber ] = useState(initialProps.partNumber);
	const [ partType, setPartType ] = useState(initialProps.partType);
	const [ partImageUrls, setPartImageUrls ] = useState(initialProps.partImageUrls);
	const [ partUrl ] = useState(initialProps.partUrl);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const toggleMenu = (event) => {
		trackSaveDropDownOpen(event);
		setIsOpen((prev) => ! prev);
	};

	const trackSaveDropDownOpen = (event) => {
		if (isOpen) {
			return;
		}
		saveDropDownOpenAnalyticsHandler(event, dropdownRef, isOpen, [ partNumber || '' ], 'Save Report', 'report');
	};

	const trackSaveDropdownSelection = (event) => {
		saveDropdownSelectionAnalyticsHandler(event, dropdownRef, 'Save Report', 'report');
	};

	// report data
	const [ buttonStyle, setButtonStyle ] = useState(initialProps.buttonStyle);
	const [ hideDropdown, setHideDropdown ] = useState(initialProps.hideDropdown ?? false);

	// listen SSO account loaded
	useEffect(() => {
		setIsLoading(true);
		const handleAccountInfoLoaded = async () => {
			const isLogged = await isAuthenticated();
			setIsLoggedIn(isLogged);
			setIsLoading(false);
		};

		document.addEventListener('timken_sso_account_info_loaded', handleAccountInfoLoaded);

		return () => {
			document.removeEventListener('timken_sso_account_info_loaded', handleAccountInfoLoaded);
		};
	}, []);

	useEffect(() => {
		if (isLoggedIn) {
			setIsLoading(projectsLoading);
		}
	}, [ projectsLoading, isLoggedIn ]);

	// listen reportSelected custom event
	useEffect(() => {
		const handleReportReady = (e: Event) => {
			const customEvent = e as CustomEvent<any>;
			const data = customEvent.detail;

			setIsResultsReady(data.ready || false);

			if ( data.ready ) {
				setPartImageUrls(data.partImageUrl);
				setPartNumber(data.partNumber);
				setPartType(data.partType);
			}

			// set hideDropdown if available; otherwise, assume is false
			if (typeof data.hideDropdown !== 'undefined') {
				setHideDropdown(data.hideDropdown);
			} else {
				setHideDropdown(false);
			}
		};

		document.addEventListener('reportReadyEvent', handleReportReady);

		return () => {
			document.removeEventListener('reportReadyEvent', handleReportReady);
		};
	}, []);

	/**
	 * Close dropdown if user clicks outside of dropdown container
	 */
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && ! dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('click', handleClickOutside);
		}

		return () => document.removeEventListener('click', handleClickOutside);
	}, [ isOpen ]);

	// Custom event when a result is ready
	useEffect(() => {
		if (hasChanged) {
			const event = new CustomEvent('reportPrintEvent', {
				detail: {
					ready: !! reportName.length,
					projectName,
					reportName,
					comments,
				},
			});

			document.dispatchEvent(event);
			setHasChanged(false);
		}
	}, [ projectName, reportName, comments, hasChanged ]);

	/**
	 * Refresh project data when a new project is created
	 *
	 * @param  createdProjectId
	 */
	const onNewProjectCreated = async (createdProjectId) => {
		const projectNameInput: HTMLInputElement | null | undefined = dropdownRef?.current?.querySelector('#project_name');
		const newProjectName = projectNameInput?.value || '';
		setProjectName(newProjectName);
		saveNewProjectClickAnalyticsHandler(dropdownRef, 'Save Report', 'report');
		await saveReportToProject(createdProjectId); // save report to the created project
	};
	const findProjectName = (projectId: string) => projects.find((projectItem) => projectItem.projectId === projectId)?.projectName;

	/**
	 * Save report to project
	 *
	 * @param  projectId
	 * @param  id
	 * @param  name
	 */
	const saveReportToProject = async (id: string, name?: string) => {
		if (! id) {
			return;
		}

		setProjectID(id);
		if (findProjectName(id) !== undefined) {
			setProjectName(findProjectName(id) || '');
		}

		setIsLoading(true);
		setIsModalOpen(true);
		setIsLoading(false);
		refreshProjects();
	};

	const handleSaveReportsClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, projectId: string) => {
		event.preventDefault();
		saveDropdownSelectionAnalyticsHandler(event, dropdownRef, 'Save Report', 'report');
		saveReportToProject(projectId);
		setIsOpen(false);
	};

	const cancelLoadingOnDelay = (delay = 5000) => {
		setTimeout(function() {
			if (loading || isLoading) {
				setLoading(false);
				setIsLoading(false);
			}
		}, delay);
	};

	const createPdfAndSaveReport = async () => {
		setLoading(true);
		const html2pdfEl = document.getElementById('t-print-report');

		if (html2pdfEl) {
			const vMargin = 0.6;
			const hMargin = 0.45;
			const html2pdfOpt = {
				filename: `${ uuidv4() }.pdf`,
				html2canvas: {
					allowTaint: true,
					letterRendering: true,
					scale: 1,
					scrollX: 0,
					scrollY: 0,
					useCORS: true,
				},
				image: {
					quality: 1,
					type: 'jpeg',
				},
				jsPDF: {
					format: 'letter',
					orientation: 'portrait',
					unit: 'in',
				},
				margin: [ vMargin, hMargin ],
			};

			await html2pdf()
				.set(html2pdfOpt)
				.from(html2pdfEl.innerHTML)
				.toPdf()
				.get('pdf')
				.then(async (pdf: any) => {
					const totalPages = pdf.internal.getNumberOfPages();
					const ppi = (72 / 96); // web ppi / figma ppi

					if (totalPages > 1) {
						for (let i = 1; i <= totalPages; i++) {
							pdf.setPage(i);
							pdf.setFontSize(Math.round(14 * ppi));
							pdf.text(
								`Page ${ i } of ${ totalPages }`,
								pdf.internal.pageSize.getWidth() - hMargin,
								pdf.internal.pageSize.getHeight() - vMargin,
								{ align: 'right' },
							);
						}
					}

					const html2pdfBlob = pdf.output('blob');
					const reader = new FileReader();

					reader.readAsArrayBuffer(html2pdfBlob);

					reader.onloadend = (evt) => {
						if (evt?.target && evt.target.readyState === FileReader.DONE) {
							const arrayBuffer = evt.target.result;

							if (arrayBuffer && arrayBuffer instanceof ArrayBuffer) {
								const byteArray = new Uint8Array(arrayBuffer);

								const byteArrayLen = byteArray.byteLength;
								let binary = '';

								for (let i = 0; i < byteArrayLen; i++) {
									binary += String.fromCharCode(byteArray[ i ]);
								}

								const base64String = btoa(binary);

								const url = window.location.pathname.replace(/\/$/, '');
								const lastSeg = url.substring(url.lastIndexOf('/') + 1);

								createProjectReports({
									projectId: projectID,
									reportName,
									reportURL: '',
									reportType: lastSeg ?? '',
									pdfByteArrayData: base64String,
									partNumber: partNumber,
									partType: partType
								}).then((response) => {
									// Handle the response here
									refreshProjects();
									setFormSuccess(true);
									setCloseText(text.close);
								}).catch((error) => {
									// Handle the error here
									setFormError(true);
									setCreateErrorMessage(error.message);
									console.error('Error creating project report:', error);
									toast.dismiss();
									toast.error(error.message);
								}).finally(() => {
									setLoading(false);
								});
							}
						}
					};
				});
		} else {
			toast.dismiss();
			toast.error('Please run result first');
		}

		cancelLoadingOnDelay();
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		resetForm();
	};

	const resetForm = () => {
		setComments('');
		setReportName('');
		setFormError(false);
		setFormSuccess(false);
		setCloseText(text.cancel);
	};

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		setCreateErrorMessage('');
		setFormError(false);
		setFormSuccess(false);
		setLoading(true);

		try {
			await createPdfAndSaveReport();
		} catch (error) {
			setFormError(true);
			setFormSuccess(false);
			setCreateErrorMessage(error.message);
			console.error('Error creating project report: ', error);
		}

		cancelLoadingOnDelay();
	};

	const handleChange = (value = '', type = '') => {
		type = type.toLowerCase();

		switch (type) {
			case 'report_name':
				setReportName(value);
				break;
			case 'comments':
				setComments(value);
				break;
		}
		setHasChanged(true);
	};

	// not render anything if hideDropdown is true
	if (hideDropdown) {
		return null;
	}

	return (
		<>
			<div
				className={`dropdown ${ isLoading ? 't-full-block-skeleton' : '' }`}
				ref={dropdownRef}
				data-item-number={partNumber}
				data-project-count={projects.length}
			>
				<ToggleButton
					buttonStyle={buttonStyle}
					isOpen={isOpen}
					isLoggedIn={isLoggedIn}
					isResultsReady={isResultsReady}
					toggleMenu={toggleMenu}
				/>

				<SaveToProjectDropDown
					projects={projects}
					isOpen={isOpen}
					targetType='reports'
					onSuccess={onNewProjectCreated}
					onModalOpen={trackSaveDropdownSelection}
					projectItemCompareValue=''
					onSaveClick={handleSaveReportsClick}>
				</SaveToProjectDropDown>
			</div>

			{/* Modal */}
			<Modal
				id='report_name_modal'
				isOpen={isModalOpen}
				onClose={handleModalClose}
			>
				<div className='t-my-dashboard__modal'>
					<h2 className={formSuccess ? 'invisible' : ''}>
						{text.createReportName}
					</h2>

					<form onSubmit={handleSubmit}>
						<TextInput
							id='report_name'
							errorMessage={formError ? createErrorMessage : ''}
							disabled={loading || formSuccess}
							invalid={formError}
							required={true}
							label='Report Name'
							name='report_name'
							placeholder=''
							value={reportName}
							className={`${ loading ? 't-full-block-skeleton' : '' } ${
								formSuccess ? 'hidden' : ''
							}`}
							onChange={(e) => {
								handleChange(e.target.value, 'report_name');
							}}
						/>

						<TextInput
							id='comments'
							disabled={loading || formSuccess}
							label='Comments'
							name='comments'
							placeholder=''
							value={comments}
							className={`${ loading ? 't-full-block-skeleton' : '' } ${
								formSuccess ? 'hidden' : ''
							}`}
							onChange={(e) => {
								handleChange(e.target.value, 'comments');
							}}
						/>

						{formSuccess && (
							<h4 className='t-my-dashboard__create-report--success'>
								Report '{reportName}' has been successfully created.
							</h4>
						)}

						<div className='modal-actions save-report'>
							{! formSuccess && (
								<button
									type='submit'
									disabled={reportName.length === 0 || loading}
									className='btn btn--primary'
								>
									{text.save}
								</button>
							)}

							<button
								type='button'
								className='btn btn--secondary save-report-close-button'
								disabled={loading}
								onClick={handleModalClose}
							>
								{closeText}
							</button>
						</div>
					</form>
				</div>
			</Modal>

			<Print partNumber={partNumber} partImageUrls={partImageUrls} partUrl={partUrl} />
		</>
	);
};

export default SaveReportDropdown;
